/**
 * Cookie `locale` — convention partagée avec les autres apps AssessFirst.
 *
 * assessments et personal-account posent l'objet locale complet de static-data
 * (`useCookies().set('locale', locale)` côté universal-cookie → JSON encodé en
 * URL) et démarrent dessus (`langInCookie?.iso_code_639`). welcome lisait
 * `NEXT_LOCALE`, un reliquat de la version Next.js que personne d'autre ne
 * comprend : on suit désormais la même convention, et welcome devient à la fois
 * lecteur et émetteur.
 *
 * Seule différence assumée avec ces deux apps : le cookie est posé ici sur le
 * domaine parent (`inferDomain`) et pas en host-only, sinon il ne franchit pas
 * la frontière entre sous-domaines — ce qui est justement le but. Le format,
 * lui, est identique.
 */

import { useCookies } from '@/composables/useCookies';
import { type CdnLocale, fetchLocales } from '@/services/cdn';

const COOKIE_NAME = 'locale';
const MAX_AGE = 31536000; // 1 an, comme l'ancien NEXT_LOCALE

/**
 * Objet locale du cookie, ou `null` s'il est absent / illisible. Le cookie
 * n'est pas httpOnly, donc son contenu n'est pas fiable : sans `iso_code_639`
 * exploitable on renvoie `null` et l'appelant retombe sur son propre défaut.
 */
export function readLocaleCookie(): CdnLocale | null {
  const locale = useCookies().getJSON<CdnLocale>(COOKIE_NAME);

  return locale?.iso_code_639 ? locale : null;
}

/**
 * Persiste une locale à partir de son code ISO. La liste static-data fournit
 * l'objet complet attendu par les autres apps ; si le CDN est injoignable on
 * écrit malgré tout le code ISO seul — dégradé mais suffisant pour le boot de
 * welcome, et les apps qui comparent l'`id` du cookie à `me.locale_id` se
 * resynchronisent d'elles-mêmes.
 */
export async function persistLocaleCookie(iso: string): Promise<void> {
  let locale = { iso_code_639: iso } as CdnLocale;

  try {
    const locales = await fetchLocales();
    locale = locales.find(l => l.iso_code_639 === iso) ?? locale;
  } catch {
    /* CDN injoignable — écriture dégradée */
  }

  useCookies().set(COOKIE_NAME, JSON.stringify(locale), { maxAge: MAX_AGE, inferDomain: true });
}
